import { useNavigate, useParams } from "react-router-dom";

import MainContainer from "../../layout/MainContainer";

import {
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useAddPaymentPlanMutation,
  useLazyPaymentPlanDetailQuery,
  useUpdatePaymentPlanMutation,
} from "../../service/paymentPlan";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { errorToast, successToast } from "../../helpers";
import { isNumberPrice, isString, isValidInput } from "../../utils/validations";
import { formatNumberWithCommas } from "../../utils/commoFunctions";
import Loader from "../../constants/Loader";

const AddPaymentPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([
    {
      id: 1,
      part_number: 1,
      totalPrice: "",
      min_price: "",
      rem_price: "",
      type: 1,
      commission: "",
    },
    {
      id: 2,
      part_number: 2,
      totalPrice: "",
      type: 1,
      min_price: "",
      rem_price: "",
      commission: "",
    },
    {
      id: 3,
      part_number: 3,
      totalPrice: "",
      type: 1,
      min_price: "",
      rem_price: "",
      commission: "",
    },
  ]);

  const [loading, setLoading] = useState(false);

  const [addPaymentPlan] = useAddPaymentPlanMutation();
  const [updatePaymentPlan] = useUpdatePaymentPlanMutation();
  const [planDetails] = useLazyPaymentPlanDetailQuery();

  const getDetails = async (id: number) => {
    setLoading(true);

    try {
      const response = await planDetails({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        const planData = response?.data?.[0];

        // Prefill formik initial values
        formik.setValues({
          plan_name: planData?.plan_name || "",
          total_price: planData?.total_price
            ? formatNumberWithCommas(planData?.total_price?.toString())
            : "",
        });

        // Prefill payments state
        const formattedPayments = planData?.payment_parts?.map((part: any) => ({
          id: part?.id,
          part_number: Number(part?.part_number),
          totalPrice: formatNumberWithCommas(part?.amount?.toString()) || "",
          min_price: formatNumberWithCommas(
            part?.min_amount?.toString() || "0"
          ),
          rem_price: formatNumberWithCommas(
            part?.rem_amount?.toString() || "0"
          ),
          type: part?.commission_for_agent_type,
          commission: formatNumberWithCommas(
            part?.commission_for_agent?.toString() || "0"
          ),
        }));

        setPayments(formattedPayments ?? []);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const numberWords = [
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Ninth",
    "Tenth",
  ];

  const payment_part = payments?.map((payment, index) => ({
    ...(id ? { id: payment?.id } : null),
    part_name: `${
      numberWords[index]?.toLowerCase() || `${index + 1}th`
    }_payment`,
    part_number: payment?.part_number,
    price: Number(payment?.totalPrice?.replace(/,/g, "")) || 0,
    commission_for_agent_type: payment?.type,
    commission_for_agent: Number(payment?.commission?.replace(/,/g, "")) || 0,
    min_price:
      index === 0 ? Number(payment?.min_price?.replace(/,/g, "")) || 0 : 0,
    rem_price:
      index === 0
        ? Number(payment?.totalPrice?.replace(/,/g, "")) -
          Number(payment?.min_price?.replace(/,/g, ""))
        : Number(payment?.totalPrice?.replace(/,/g, "")),
  }));

  // const handlePriceInputChange = (
  //   id: number,
  //   field: string,
  //   value: string | number
  // ) => {
  //   let numericValue = value.toString().replace(/,/g, ""); // Remove commas for parsing
  //   if (isNumberPrice(numericValue)) {
  //     const formattedValue = formatNumberWithCommas(numericValue);
  //     setPayments((prevPayments) =>
  //       prevPayments.map((payment) =>
  //         payment.id === id ? { ...payment, [field]: formattedValue } : payment
  //       )
  //     );
  //   }
  // };

  const handlePriceInputChange = (
    id: number,
    field: string,
    value: string | number
  ) => {
    const numericValue = value.toString().replace(/,/g, ""); // Remove commas

    if (numericValue === "" || isNumberPrice(numericValue)) {
      // If it's 'commission', don't format with commas
      const formattedValue =
        field === "commission"
          ? numericValue
          : formatNumberWithCommas(numericValue);

      setPayments((prevPayments) =>
        prevPayments.map((payment) =>
          payment.id === id ? { ...payment, [field]: formattedValue } : payment
        )
      );
    }
  };

  const handleInputChange = (
    id: number,
    field: string,
    value: string | number
  ) => {
    setPayments((prevPayments) =>
      prevPayments?.map((payment) =>
        payment?.id === id
          ? {
              ...payment,
              [field]: value,
              ...(field === "type" ? { commission: "" } : {}), // Reset commission when type change
            }
          : payment
      )
    );
  };
  const addPayment = () => {
    const isValidPayments = payments.every(
      (payment) =>
        payment.totalPrice &&
        (payment.id !== 1 || payment.min_price) && // min_price required for first payment
        payment.commission
    );

    if (!isValidPayments) {
      errorToast("Please fill all required payment fields.");
      formik.setSubmitting(false);
      return;
    }

    setPayments([
      ...payments,
      {
        id: payments?.length + 1,
        part_number: payments?.length + 1,
        totalPrice: "",
        type: 1,
        min_price: "",
        rem_price: "",
        commission: "",
      },
    ]);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      plan_name: "",
      total_price: "",
    },

    validationSchema: Yup.object({
      plan_name: Yup.string()
        .required("Plan Name is required")
        .min(2, "Minimum 2 characters required"),
      total_price: Yup.string().required("Total Price is required"),
      visa_type: Yup.string().notOneOf(["default"], "Visa Type is required"),
      payments: Yup.array().of(
        Yup.object().shape({
          totalPrice: Yup.string().required("Total price is required"),
          min_price: Yup.string()
            .when("index", {
              is: 0,
              then: Yup.string().required("Minimum price is required"),
            })
            .test({
              name: "min-price-less-than-totalPrice",
              message: "Minimum price cannot be more than Total Price",
              test: function (value: string) {
                const totalPrice = this.parent.totalPrice;
                if (!value || !totalPrice) return true; // Skip validation if values are missing
                return parseFloat(value) <= parseFloat(totalPrice);
              },
            }),
          commission: Yup.string().required("Commission is required"),
        })
      ),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const isValidPayments = payments.every(
        (payment) =>
          payment.totalPrice &&
          (payment.id !== 1 || payment.min_price) && // min_price required for first payment
          payment.commission
      );

      if (!isValidPayments) {
        errorToast("Please fill all required payment fields.");
        formik.setSubmitting(false);
        return;
      }

      // if (visaType === "default") {
      //   return;
      // }
      const sumOfParts = payment_part.reduce(
        (sum, part) => sum + Number(part.price),
        0
      );
      const PaymentPlanPrice = Number(values.total_price?.replace(/,/g, ""));
      if (sumOfParts !== PaymentPlanPrice) {
        errorToast(
          "The sum of all payments should be equal to total price of the payment plan"
        );
        return;
      }

      const body = {
        // visa_type: Number(visaType),
        plan_name: values.plan_name,
        total_price: Number(values.total_price?.replace(/,/g, "")),
        is_there_commission_for_agent: true,
        is_there_commission_for_doc_verifier: false,
        is_there_commission_for_partner: false,
        is_there_commission_for_partner_companies: false,
        payment_part: payment_part,
      };

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updatePaymentPlan({
            body: encryptedBody,
            id: Number(id),
          }).unwrap();
        } else {
          response = await addPaymentPlan(encryptedBody).unwrap();
        }

        if (response?.status === 200) {
          setLoading(false);
          if (id) {
            successToast("Payment plan updated successfully");
          } else {
            successToast("Payment plan added successfully");
          }
          navigate("/manage-payments/payment-plans");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);
  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Payment Plan</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/manage-payments/payment-plans")}>
              Manage Payments
            </span>{" "}
            - {id ? "Edit" : "Add"} Payment Plan
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Plan Name</label>
              <TextField
                variant="outlined"
                placeholder="Plan Name"
                fullWidth
                hiddenLabel
                className="text_field"
                name="plan_name"
                id="plan_name"
                inputProps={{ maxLength: 30 }}
                value={formik.values.plan_name}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.plan_name && formik.errors.plan_name}
              />
            </div>
            <div className="control_group w_50">
              <label>Total Price</label>
              <TextField
                variant="outlined"
                placeholder="Total Price"
                fullWidth
                hiddenLabel
                className="text_field"
                name="total_price"
                id="total_price"
                inputProps={{ maxLength: 9 }}
                value={formik.values.total_price}
                onChange={(val) => {
                  const { value } = val.target;
                  // Remove commas for proper validation and length counting
                  let numericValue = value.replace(/,/g, "");

                  if (numericValue === "") {
                    formik.setFieldValue("total_price", "");
                    return;
                  }
                  // Prevent starting with 0 unless it's just "0"
                  if (numericValue.length && numericValue.startsWith("0")) {
                    numericValue = numericValue.replace(/^0+/, ""); // Remove leading zeros
                  }

                  // Validate number format and length limit
                  if (isNumberPrice(numericValue) && numericValue.length <= 9) {
                    const formattedValue = formatNumberWithCommas(numericValue);
                    formik.setFieldValue("total_price", formattedValue);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.total_price && formik.errors.total_price
                }
              />
            </div>

            {payments?.map((payment, index) => (
              <div key={payment.id}>
                <h3
                  style={{
                    paddingBottom: "6px",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {numberWords[index] || `${index + 1}th`} Payment
                </h3>
                <div className="gap_p">
                  <div className="control_group w_50">
                    <label>Total Price</label>
                    <TextField
                      hiddenLabel
                      variant="outlined"
                      placeholder="Total Price"
                      fullWidth
                      value={payment?.totalPrice}
                      inputProps={{ maxLength: 11 }}
                      onChange={(e) => {
                        const newTotal = e.target.value;
                        const totalValue = parseFloat(
                          newTotal.replace(/,/g, "")
                        );
                        const minValue = parseFloat(
                          payment.min_price?.replace(/,/g, "")
                        );
                        const commissionValue = parseFloat(
                          payment.commission?.replace(/,/g, "")
                        );

                        // If min price is greater than or equal to new total, reset it
                        if (
                          !isNaN(minValue) &&
                          !isNaN(totalValue) &&
                          minValue >= totalValue
                        ) {
                          handlePriceInputChange(payment.id, "min_price", "");
                        }
                        if (
                          !isNaN(commissionValue) &&
                          !isNaN(totalValue) &&
                          commissionValue > totalValue
                        ) {
                          handlePriceInputChange(payment.id, "commission", "");
                        }

                        // Update total price
                        handlePriceInputChange(
                          payment.id,
                          "totalPrice",
                          newTotal
                        );
                      }}
                      error={
                        formik.touched.payments?.[index]?.totalPrice &&
                        Boolean(formik.errors.payments?.[index]?.totalPrice)
                      }
                      helperText={
                        formik.touched.payments?.[index]?.totalPrice &&
                        formik.errors.payments?.[index]?.totalPrice
                      }
                    />
                  </div>
                  {index === 0 ? (
                    <div className="control_group w_50">
                      <label>Minimum Price</label>
                      <TextField
                        hiddenLabel
                        variant="outlined"
                        placeholder="Minimum Price"
                        fullWidth
                        value={payment.min_price}
                        inputProps={{ maxLength: 11 }}
                        onChange={(e) => {
                          const value = e.target.value;

                          // Validate min_price and show toast if it's invalid
                          if (
                            parseFloat(value?.replace(/,/g, "")) >=
                            parseFloat(payment.totalPrice?.replace(/,/g, ""))
                          ) {
                            errorToast(
                              "Minimum Price cannot be greater than or equal to Total Price!"
                            );
                            return;
                          }

                          // Call your existing price change handler
                          handlePriceInputChange(
                            payment.id,
                            "min_price",
                            value
                          );
                        }}
                        error={
                          formik.touched.payments?.[index]?.min_price &&
                          Boolean(formik.errors.payments?.[index]?.min_price)
                        }
                        helperText={
                          formik.touched.payments?.[index]?.min_price &&
                          formik.errors.payments?.[index]?.min_price
                        }
                      />
                    </div>
                  ) : null}
                  <div className="control_group w_50">
                    <label>Commission Type</label>
                    <Select
                      fullWidth
                      value={payment.type}
                      onChange={(e) =>
                        handleInputChange(payment.id, "type", e.target.value)
                      }
                    >
                      <MenuItem value={1}>Percentage</MenuItem>
                      <MenuItem value={2}>Flat</MenuItem>
                    </Select>
                  </div>
                  <div className="control_group w_50">
                    <label>Commission</label>
                    <TextField
                      hiddenLabel
                      variant="outlined"
                      placeholder="Commission"
                      fullWidth
                      value={payment.commission}
                      inputProps={{ maxLength: payment?.type === 1 ? 5 : 11 }}
                      onChange={(e) => {
                        let value = e.target.value;

                        // Restrict value to 100 if type === 1
                        if (payment?.type === 1 && parseFloat(value) > 100) {
                          errorToast("Commission cannot be more than 100%");

                          value = "100";
                        } else {
                          // Validate min_price and show toast if it's invalid
                          if (
                            parseFloat(value?.replace(/,/g, "")) >
                            parseFloat(payment.totalPrice?.replace(/,/g, ""))
                          ) {
                            errorToast(
                              "Commission cannot be greater than the Total Price!"
                            );
                            return;
                          }

                          // Call your existing price change handler
                          handlePriceInputChange(
                            payment.id,
                            "commission",
                            value
                          );
                        }

                        //

                        //
                      }}
                      InputProps={{
                        endAdornment:
                          payment?.type === 1 ? (
                            <InputAdornment position="end">%</InputAdornment>
                          ) : null,
                      }}
                      error={
                        formik.touched.payments?.[index]?.commission &&
                        Boolean(formik.errors.payments?.[index]?.commission)
                      }
                      helperText={
                        formik.touched.payments?.[index]?.commission &&
                        formik.errors.payments?.[index]?.commission
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="form_end_btn"
            style={{ display: "flex", gap: "10px" }}
          >
            <Button
              variant="contained"
              className="btn btn_secondary"
              onClick={addPayment}
            >
              Add Payment
            </Button>
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddPaymentPlan;
